import React, { useState } from "react";
import { useBandWorkspace } from "../contexts/BandWorkspaceContext";

export default function SetlistsTab() {
  const {
    user,
    songs: songsCtx,
    setlists: setlistsCtx,
    liveMode: liveModeCtx,
    setActiveTab,
  } = useBandWorkspace();

  const { songs } = songsCtx;

  const {
    setlists,
    isSetlistsLoading,
    setlistsError,
    editingSetlistId,
    downloadingSetlistId,
    handleRenameSetlist,
    handleEditSetlist,
    handleDeleteSetlist,
    handleLockSetlist,
    handleUnlockSetlist,
    mergePDFs,
  } = setlistsCtx;

  const {
    handleGoLive,
    isSetlistOfflineReady,
    isSetlistStale,
    handlePrepareOffline,
    preparingOfflineSetlistId,
  } = liveModeCtx;

  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const [unlockTargetId, setUnlockTargetId] = useState(null);
  const [lockPrepPromptId, setLockPrepPromptId] = useState(null);
  const [goLivePromptSetlist, setGoLivePromptSetlist] = useState(null);

  const showNudge =
    !nudgeDismissed &&
    setlists.some((s) => (s.songIds?.length ?? 0) > 0 && !isSetlistOfflineReady(s.id));

  const handleGoLiveClick = (setlist) => {
    if (user.role === "admin" && !setlist.locked) {
      setGoLivePromptSetlist(setlist);
    } else {
      handleGoLive(setlist);
    }
  };

  const handleLockClick = async (setlist) => {
    const success = await handleLockSetlist(setlist.id);
    if (success && !isSetlistOfflineReady(setlist.id)) {
      setLockPrepPromptId(setlist.id);
    }
  };

  const unlockTarget = unlockTargetId ? setlists.find((s) => s.id === unlockTargetId) : null;
  const lockPrepTarget = lockPrepPromptId ? setlists.find((s) => s.id === lockPrepPromptId) : null;

  return (
    <section className="px-4 py-6">
      {/* Go Live without lock prompt */}
      {goLivePromptSetlist && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-sm rounded-lg border border-border bg-surface p-6 shadow-xl">
            <h3 className="mb-2 text-base font-semibold text-text-primary">
              This setlist isn&rsquo;t locked
            </h3>
            <p className="mb-5 text-sm text-text-secondary">
              Someone could change the order or drop a song while you&rsquo;re
              playing. Lock it now so what you rehearsed is what you play.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  handleGoLive(goLivePromptSetlist);
                  setGoLivePromptSetlist(null);
                }}
                className="flex-1 rounded-md border border-border bg-base px-4 py-2 text-sm font-medium text-text-secondary hover:text-text-primary"
              >
                Go live anyway
              </button>
              <button
                type="button"
                onClick={async () => {
                  await handleLockSetlist(goLivePromptSetlist.id);
                  handleGoLive(goLivePromptSetlist);
                  setGoLivePromptSetlist(null);
                }}
                className="flex-1 rounded-md border border-accent/30 bg-accent/10 px-4 py-2 text-sm font-medium text-accent hover:bg-accent/15"
              >
                Lock and go live
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unlock confirmation modal */}
      {unlockTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-sm rounded-lg border border-border bg-surface p-6 shadow-xl">
            <h3 className="mb-2 text-base font-semibold text-text-primary">
              Unlock &ldquo;{unlockTarget.name}&rdquo;?
            </h3>
            <p className="mb-3 text-sm text-text-secondary">Once unlocked, any admin can:</p>
            <ul className="mb-4 list-disc space-y-1 pl-5 text-sm text-text-secondary">
              <li>Change which songs are in the setlist</li>
              <li>Reorder or remove songs</li>
              <li>Rename the setlist</li>
            </ul>
            <p className="mb-5 text-sm text-text-secondary">
              The version your band prepared may no longer match what they see on stage.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setUnlockTargetId(null)}
                className="flex-1 rounded-md border border-border bg-base px-4 py-2 text-sm font-medium text-text-primary hover:bg-surface"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  handleUnlockSetlist(unlockTarget.id);
                  setUnlockTargetId(null);
                }}
                className="flex-1 rounded-md border border-border bg-base px-4 py-2 text-sm font-medium text-text-primary hover:bg-surface"
              >
                Unlock setlist
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Post-lock offline prep prompt */}
      {lockPrepTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-sm rounded-lg border border-border bg-surface p-6 shadow-xl">
            <h3 className="mb-2 text-base font-semibold text-text-primary">Setlist locked</h3>
            <p className="mb-5 text-sm text-text-secondary">
              Prepare it offline now so charts load at the venue — even without signal.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setLockPrepPromptId(null)}
                className="flex-1 rounded-md border border-border bg-base px-4 py-2 text-sm font-medium text-text-secondary hover:text-text-primary"
              >
                Later
              </button>
              <button
                type="button"
                onClick={() => {
                  handlePrepareOffline(lockPrepTarget);
                  setLockPrepPromptId(null);
                }}
                className="flex-1 rounded-md border border-accent/30 bg-accent/10 px-4 py-2 text-sm font-medium text-accent hover:bg-accent/15"
              >
                Prepare offline
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-md border border-border bg-surface p-4">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-medium text-text-primary">Setlists</h2>
        </div>

        {setlistsError && (
          <p className="mb-3 text-sm text-danger">{setlistsError}</p>
        )}

        {isSetlistsLoading ? (
          <p className="text-sm text-text-secondary">Loading setlists...</p>
        ) : setlists.length === 0 ? (
          <div className="flex flex-col items-center py-10 text-center">
            <svg className="mb-4 h-10 w-10 text-text-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
              <rect x="9" y="3" width="6" height="4" rx="1" />
              <line x1="9" y1="12" x2="15" y2="12" />
              <line x1="9" y1="16" x2="13" y2="16" />
            </svg>
            {user.role === "admin" ? (
              <>
                <h3 className="mb-2 text-base font-medium text-text-primary">
                  Setlists are built from your song library
                </h3>
                <p className="mb-6 max-w-xs text-sm text-text-secondary">
                  Head to the Songs tab and click &lsquo;Add&rsquo; on any song to start building a setlist. Once saved, your setlists will appear here for the whole band.
                </p>
                <button
                  onClick={() => setActiveTab("songs")}
                  className="rounded-md bg-accent px-5 py-2.5 text-sm font-medium text-white border-transparent hover:bg-accent/85"
                >
                  Go to Songs →
                </button>
              </>
            ) : (
              <>
                <h3 className="mb-2 text-base font-medium text-text-primary">No setlists yet</h3>
                <p className="mb-6 max-w-xs text-sm text-text-secondary">
                  Your band admin builds setlists from the song library. Once they&rsquo;re saved, they&rsquo;ll show up here — and you can tap any song to pull up the sheet music.
                </p>
              </>
            )}
          </div>
        ) : (
          <>
            {showNudge && (
              <div className="mb-3 flex items-start justify-between gap-3 rounded-md border border-brand-gold/40 bg-brand-gold/5 px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-brand-gold">
                    Prepare your setlists offline before the show — charts won't load without signal.
                  </p>
                  <p className="mt-0.5 text-xs text-text-secondary">
                    Tap &lsquo;Prepare offline&rsquo; on each setlist you&rsquo;re playing. Each device needs to do this independently.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setNudgeDismissed(true)}
                  aria-label="Dismiss"
                  className="shrink-0 text-text-secondary hover:text-text-primary"
                >
                  ×
                </button>
              </div>
            )}
            <ul className="space-y-2">
              {setlists.map((setlist) => {
                const createdLabel = setlist?.createdAt?.toDate
                  ? setlist.createdAt.toDate().toLocaleDateString()
                  : "Unknown date";
                const songCount = Array.isArray(setlist.songIds) ? setlist.songIds.length : 0;
                const isBeingEdited = editingSetlistId === setlist.id;
                const isLocked = !!setlist.locked;
                const setlistSongs = (setlist.songIds ?? [])
                  .map((id) => songs.find((s) => s.id === id))
                  .filter(Boolean);
                const isPreparing = preparingOfflineSetlistId === setlist.id;
                const isOfflineReady = isSetlistOfflineReady(setlist.id);
                const isStale = isSetlistStale(setlist, setlistSongs);

                return (
                  <li
                    key={setlist.id}
                    className={`flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 ${
                      isBeingEdited ? "border-accent bg-accent/10" : "border-border bg-base"
                    }`}
                  >
                    <div className="min-w-[180px] flex-1">
                      <p className="text-sm font-medium text-text-primary">
                        {isLocked && (
                          <span className="mr-1.5 text-text-secondary" aria-label="Locked" title="Setlist is locked">
                            🔒
                          </span>
                        )}
                        {setlist.name || "Untitled setlist"}
                        {isBeingEdited && <span className="ml-2 text-xs text-accent">editing</span>}
                      </p>
                      <p className="text-xs text-text-secondary">
                        {songCount} song{songCount === 1 ? "" : "s"}
                        {setlist.performanceDate && (
                          <> • {new Date(setlist.performanceDate + "T00:00:00").toLocaleDateString()}</>
                        )}
                        {" • "}{createdLabel}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleGoLiveClick(setlist)}
                      className="border-accent/40 bg-accent/8 text-accent hover:bg-accent/15"
                    >
                      Go Live
                    </button>

                    {songCount > 0 && (
                      isPreparing ? (
                        <button
                          type="button"
                          disabled
                          className="border-subtle text-xs text-text-secondary disabled:opacity-50"
                        >
                          <span className="mr-1 inline-block animate-spin">⟳</span>Preparing…
                        </button>
                      ) : isStale ? (
                        <>
                          <span className="inline-flex items-center gap-1 rounded border border-brand-gold/40 bg-brand-gold/10 px-2 py-1 text-xs text-brand-gold">
                            ⚠ Charts updated
                          </span>
                          <button
                            type="button"
                            onClick={() => handlePrepareOffline(setlist)}
                            className="border-subtle text-xs text-text-secondary hover:text-text-primary"
                            title="A chart was updated — re-prepare before the show"
                          >
                            Refresh offline
                          </button>
                        </>
                      ) : isOfflineReady ? (
                        <span className="border-subtle text-xs text-text-secondary px-2 py-1 rounded border">
                          {isLocked ? "🔒 " : ""}✓ Offline ready
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handlePrepareOffline(setlist)}
                          className="border-subtle text-xs text-text-secondary hover:text-text-primary"
                        >
                          Prepare offline
                        </button>
                      )
                    )}

                    <button
                      type="button"
                      onClick={() => mergePDFs(setlist.songIds ?? [], setlist.id, setlist.name)}
                      disabled={downloadingSetlistId === setlist.id}
                      className="border-subtle text-xs text-text-secondary hover:text-text-primary disabled:opacity-50"
                    >
                      {downloadingSetlistId === setlist.id ? "Building…" : "Download PDF"}
                    </button>

                    {user.role === "admin" && (
                      <>
                        <button
                          type="button"
                          onClick={() => !isLocked && handleEditSetlist(setlist)}
                          disabled={isLocked}
                          className={isLocked ? "opacity-40 cursor-not-allowed" : undefined}
                          title={isLocked ? "Unlock to edit" : undefined}
                        >
                          {isBeingEdited ? "Reload" : "Edit"}
                        </button>

                        <button
                          type="button"
                          onClick={() => !isLocked && handleRenameSetlist(setlist)}
                          disabled={isLocked}
                          className={isLocked ? "opacity-40 cursor-not-allowed" : undefined}
                          title={isLocked ? "Unlock to rename" : undefined}
                        >
                          Rename
                        </button>

                        {isLocked ? (
                          <button
                            type="button"
                            onClick={() => setUnlockTargetId(setlist.id)}
                            className="border-subtle text-xs text-text-secondary hover:text-text-primary"
                          >
                            Unlock
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleLockClick(setlist)}
                            className="border-subtle text-xs text-text-secondary hover:text-text-primary"
                          >
                            Lock
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={() => !isLocked && handleDeleteSetlist(setlist.id, setlist.name)}
                          disabled={isLocked}
                          className={`border-danger/30 text-danger${isLocked ? " opacity-40 cursor-not-allowed" : ""}`}
                          title={isLocked ? "Unlock to delete" : undefined}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </section>
  );
}
